import {
  CustomSize,
  DialogButtons,
  DialogCustomButton,
  DialogCustomButtonType,
  DialogResults,
  ModalRef,
  useTranslation,
} from '@msdk/common'
import {
  Box,
  ButtonAlternative,
  ButtonIcon,
  ButtonPrimary,
  ButtonSecondary,
  CloseIcon,
  createComponentWrapperWithRef,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider as UiDivider,
  styled,
  TextField,
  THEME_CONST,
  Typography,
} from '@msdk/ui'
import React, { ChangeEvent, useEffect, useState } from 'react'

const TextFieldStyled = styled(TextField)`
  padding-top: 10px;
  width: 100%;
`

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-height: 56px;
  padding: 16px 24px;
`
const StyledMessageTypography = styled(Typography)`
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-line;
`

interface ContentProps {
  $customWidth?: string
  $customHeight?: string
}

// added customWidth and customHeight to Content styled component
// width defaults to 444px if customWidth is not provided
// height is optional
const Content = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$customWidth' && prop !== '$customHeight',
})<ContentProps>`
  padding: ${THEME_CONST.defaultPadding}px;
  overflow-y: auto;

  width: ${({ $customWidth }) => ($customWidth ? $customWidth : '444px')};
  ${({ $customHeight }) => ($customHeight ? `height: ${$customHeight};` : '')}
`

/* Divider */
const Divider = createComponentWrapperWithRef(
  {
    mui: UiDivider,
    nova: () => null,
  },
  'Divider',
)

export enum TextAlignment {
  Left = 'left',
  Right = 'right',
  Center = 'center',
  Justify = 'justify',
}

export interface UICustomization {
  messageAlignment: TextAlignment
}

interface Props {
  title: string
  message: string
  buttons?: DialogButtons
  showInput?: boolean
  onDimiss: (args?: DialogResults | string) => void
  modalRef?: ModalRef
  customButtons?: DialogCustomButton[]
  uiCustomization?: UICustomization
  customSize?: CustomSize
  mandatoryInput?: boolean
  stackIndex?: number
}

export function DialogPopup({
  title,
  message,
  buttons = DialogButtons.Ok,
  showInput,
  onDimiss,
  modalRef,
  customButtons,
  uiCustomization,
  customSize,
  mandatoryInput = false,
  stackIndex = 0,
}: Props) {
  const t = useTranslation()
  const [text, setText] = useState('')

  const handleClose = (result: DialogResults) => {
    if (showInput) {
      onDimiss(result === DialogResults.Ok ? text : undefined)
    } else {
      onDimiss(result)
    }
    modalRef?.close()
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setText(e.target.value)
  }

  const renderButton = (button: DialogCustomButton) => {
    const isConfirmationButton = [DialogResults.Ok, DialogResults.Yes].includes(button.result)
    const hasEmptyInput = text.trim() === ''
    const ButtonComponent =
      button.type === DialogCustomButtonType.Alternative
        ? ButtonAlternative
        : button.type === DialogCustomButtonType.Secondary
        ? ButtonSecondary
        : ButtonPrimary
    return (
      <ButtonComponent
        key={button.result}
        onClick={() => handleClose(button.result)}
        disabled={mandatoryInput && isConfirmationButton && hasEmptyInput}
      >
        <Typography variant="button">{button.label}</Typography>
      </ButtonComponent>
    )
  }

  const createButton = (label: string, result: DialogResults, type: DialogCustomButtonType): DialogCustomButton => ({
    label,
    result,
    type,
  })

  const getButtons = (): DialogCustomButton[] => {
    if (buttons === DialogButtons.Custom) {
      if (!customButtons || customButtons.length === 0) {
        throw new Error('[DialogPopup] Custom buttons must be provided when buttons is set to Custom')
      }
      return customButtons
    }

    if (showInput) {
      return [
        createButton(t('CANCEL_BUTTON_LABEL', 'Cancel'), DialogResults.Cancel, DialogCustomButtonType.Secondary),
        createButton(t('OK_BUTTON_LABEL', 'Ok'), DialogResults.Ok, DialogCustomButtonType.Primary),
      ]
    }

    switch (buttons) {
      case DialogButtons.Ok:
        return [createButton(t('OK_BUTTON_LABEL', 'Ok'), DialogResults.Ok, DialogCustomButtonType.Primary)]
      case DialogButtons.OkCancel:
        return [
          createButton(t('CANCEL_BUTTON_LABEL', 'Cancel'), DialogResults.Cancel, DialogCustomButtonType.Secondary),
          createButton(t('OK_BUTTON_LABEL', 'Ok'), DialogResults.Ok, DialogCustomButtonType.Primary),
        ]
      case DialogButtons.Yes:
        return [createButton(t('YES_BUTTON_LABEL', 'Yes'), DialogResults.Yes, DialogCustomButtonType.Primary)]
      case DialogButtons.YesNo:
        return [
          createButton(t('NO_BUTTON_LABEL', 'No'), DialogResults.No, DialogCustomButtonType.Secondary),
          createButton(t('YES_BUTTON_LABEL', 'Yes'), DialogResults.Yes, DialogCustomButtonType.Primary),
        ]
      default:
        throw new Error(`[DialogModal] unknown button type: ${buttons}`)
    }
  }

  const getDialogActions = () => {
    const actions = getButtons().map((button) => renderButton(button))
    return <DialogActions>{actions}</DialogActions>
  }

  useEffect(() => {
    const content = document.querySelector('.MuiDialogContent-root') as HTMLElement
    if (content) {
      content.style.padding = '0'
    }
  }, [])

  return (
    <Dialog
      open={modalRef?.status === 'Open'}
      sx={{ zIndex: (theme) => theme.zIndex.modal + stackIndex }}
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      <StyledDialogTitle>
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <ButtonIcon onClick={() => handleClose(DialogResults.Cancel)} aria-label="close">
          <CloseIcon />
        </ButtonIcon>
      </StyledDialogTitle>
      <Divider />
      <Content $customWidth={customSize?.width} $customHeight={customSize?.height}>
        <StyledMessageTypography variant="body1" align={uiCustomization?.messageAlignment}>
          {message}
        </StyledMessageTypography>
        {showInput && <TextFieldStyled value={text} onChange={handleInputChange} data-testid="dialog-input" />}
      </Content>
      <Divider />
      {getDialogActions()}
    </Dialog>
  )
}
